import { type CSSProperties, type ReactElement, useCallback, useEffect, useRef, useState } from 'react';

import classnames from 'classnames';

import { computeFont } from '../utils/fonts';

import OTPInput from '../otpInput';
import * as styles from './Login.css';
import type { LoginElementProps } from './Login.types';

export function LoginElement(props: LoginElementProps): ReactElement | null {
  const {
    element,
    children,
    attributes,
    className,
    isSubmitting,
    labels,
    searchParams,
    onSubmit,
    showPasswordField: showPasswordFieldProp,
    setShowPasswordField: setShowPasswordFieldProp,
  } = props;

  const {
    height,
    width,
    mobile_hide,
    desktop_hide,
    customAttributes,
    borderWidth,
    borderRadius,
    borderColor,
    borderStyle,
    padding,
    backgroundColor,
    gap,
    inputBorderWidth,
    inputBorderRadius,
    inputBorderColor,
    inputBackgroundColor,
    inputBorderStyle,
    inputTextColor,
    inputPlaceholderColor,
    inputLabelFontSize,
    inputPlaceholderFontSize,
    emailInputPlaceholder,
    passwordInputPlaceholder,
    buttonBorderWidth,
    buttonBorderRadius,
    buttonBorderColor,
    buttonBorderStyle,
    buttonBackgroundColor,
    buttonTextColor,
    buttonFontSize,
    buttonText,
    buttonSubmittingText,
    textColor,
    fontFamily,
  } = element.attrs || {};

  const containerRef = useRef<HTMLDivElement>(null);
  const otp = searchParams?.has('otp');
  const email = searchParams?.get('email') || '';
  const [showPasswordFieldLocal, setShowPasswordFieldLocal] = useState(false);
  const [has_subscription_password, setHasSubscriptionPassword] = useState(false);

  // Use prop if provided, otherwise use local state
  const showPasswordField = showPasswordFieldProp ?? showPasswordFieldLocal;
  const setShowPasswordField = setShowPasswordFieldProp ?? setShowPasswordFieldLocal;
  const togglePasswordField = () => setShowPasswordField(!showPasswordField);

  const showInputLabels =
    typeof element.attrs.showInputLabels === 'undefined' || element.attrs.showInputLabels === true;
  const emailInputLabel = element.attrs.emailInputLabel || 'Email';
  const passwordInputLabel = element.attrs.passwordInputLabel || 'Password';
  const [initialized, setInitialized] = useState(false);

  const { login_with_otp_label, set_new_password, login_with_password_label } = labels || {};

  useEffect(() => {
    if (initialized) return;
    const form = containerRef.current?.closest('form');
    if (form) {
      setHasSubscriptionPassword(form.has_subscription_password?.value === 'true');
      setInitialized(true);
    }
  });
  const buttonLabel = login_with_otp_label;

  // Handle Enter key submission
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !isSubmitting) {
      e.preventDefault();
      const form = containerRef.current?.closest('form') as HTMLFormElement;
      if (form && onSubmit) {
        const formData = new FormData(form);
        onSubmit(formData);
      }
    }
  };
  return (
    <div
      ref={containerRef}
      className="relative w-full sm:min-w-72"
      {...(Object.hasOwn(attributes || {}, 'data-node-view-wrapper') && { 'data-node-view-wrapper': '' })}
    >
      {otp ? (
        <OneTimePassword {...props} email={email} />
      ) : (
        <div
          {...customAttributes}
          className={classnames(
            className,
            {
              'mobile:hidden': mobile_hide,
              'desktop:hidden': desktop_hide,
              'pointer-events-none': isSubmitting,
            },
            styles.loginElement,
            customAttributes?.className,
            'flex flex-col gap-2',
          )}
          style={
            {
              width: '100%',
              maxWidth: width,
              gap: gap,
              color: textColor,
              borderColor: borderColor,
              borderWidth: borderWidth,
              borderRadius: borderRadius,
              borderStyle: borderStyle,
              padding: padding,
              backgroundColor: backgroundColor,
              boxSizing: 'border-box',
              overflow: 'hidden',
              fontFamily: computeFont(fontFamily) || 'inherit',
              '--input-background-color': inputBackgroundColor,
            } as CSSProperties
          }
          {...attributes}
        >
          <input type="hidden" value={showPasswordField ? 'false' : 'true'} name="request_magic_link" />
          <div className="flex flex-col w-full text-left gap-y-1">
            {showInputLabels && (
              <label htmlFor="login_email" style={{ fontSize: inputLabelFontSize } as CSSProperties}>
                {emailInputLabel}
              </label>
            )}
            {/* leave type="text" due to issues with sparkloop integration, which looks for type="email" */}
            <input
              type="text"
              name="login_email"
              required
              className={classnames(styles.input, 'w-full pr-4 cursor-text pl-4')}
              style={
                {
                  borderColor: inputBorderColor,
                  borderWidth: inputBorderWidth,
                  borderRadius: inputBorderRadius,
                  borderStyle: inputBorderStyle,
                  height: height || '50px',
                  color: inputTextColor,
                  '--placeholder-color': inputPlaceholderColor,
                  '--placeholder-font-size': inputPlaceholderFontSize,
                } as CSSProperties
              }
              placeholder={emailInputPlaceholder}
              onKeyDown={handleKeyDown}
            />
            {!showPasswordField && has_subscription_password && (
              <div className="flex flex-row justify-end w-full">
                <button type="button" onClick={togglePasswordField} disabled={isSubmitting} className="text-sm">
                  {login_with_password_label}
                </button>
              </div>
            )}
          </div>
          {showPasswordField && has_subscription_password && (
            <div className="flex flex-col text-left gap-y-1">
              {showInputLabels && (
                <label htmlFor="password" style={{ fontSize: inputLabelFontSize } as CSSProperties}>
                  {passwordInputLabel}
                </label>
              )}
              <input
                type="password"
                name="password"
                required
                className={classnames(styles.input, 'w-full pr-4 cursor-text pl-4')}
                style={
                  {
                    borderColor: inputBorderColor,
                    borderWidth: inputBorderWidth,
                    borderRadius: inputBorderRadius,
                    borderStyle: inputBorderStyle,
                    height: height || '50px',
                    color: inputTextColor,
                    '--placeholder-color': inputPlaceholderColor,
                    '--placeholder-font-size': inputPlaceholderFontSize,
                  } as CSSProperties
                }
                placeholder={passwordInputPlaceholder}
                onKeyDown={handleKeyDown}
              />
              {showPasswordField && (
                <div className="flex flex-row justify-between w-full gap-6">
                  <a href="/reset_password" className="text-sm">
                    {set_new_password}
                  </a>
                  <button type="button" onClick={togglePasswordField} className="text-sm text-right">
                    {buttonLabel}
                  </button>
                </div>
              )}
            </div>
          )}
          <button
            type="button"
            onClick={() => {
              // Use type="button" to prevent native form submit event that sparkloop intercepts
              const form = containerRef.current?.closest('form') as HTMLFormElement;
              if (form && onSubmit) {
                const formData = new FormData(form);
                onSubmit(formData);
              }
            }}
            disabled={isSubmitting}
            className="cursor-pointer disabled:opacity-50 disabled:pointer-events-none"
            style={
              {
                borderRadius: buttonBorderRadius,
                borderColor: buttonBorderColor,
                borderWidth: buttonBorderWidth,
                borderStyle: buttonBorderStyle,
                backgroundColor: buttonBackgroundColor,
                color: buttonTextColor,
                fontSize: buttonFontSize,
                boxSizing: 'border-box',
                height: height || '50px',
                padding: '0 16px 0 16px',
              } as CSSProperties
            }
          >
            {isSubmitting ? buttonSubmittingText : buttonText}
          </button>

          {children}
        </div>
      )}
    </div>
  );
}

function OneTimePassword(props: LoginElementProps & { email: string }): ReactElement | null {
  const { element, children, attributes, className, isSubmitting, email, labels } = props;

  const {
    height,
    width,
    mobile_hide,
    desktop_hide,
    customAttributes,
    borderWidth,
    borderRadius,
    borderColor,
    borderStyle,
    padding,
    backgroundColor,
    gap,
    inputBackgroundColor,
    buttonBorderWidth,
    buttonBorderRadius,
    buttonBorderColor,
    buttonBorderStyle,
    buttonBackgroundColor,
    buttonTextColor,
    buttonFontSize,
    buttonText,
    buttonSubmittingText,
    textColor,
    fontFamily,
  } = element.attrs || {};
  const { otp_label, resend_otp_button_label } = labels || {};

  const submitRef = useRef<HTMLInputElement>(null);
  const handleSubmit = useCallback(() => {
    if (submitRef.current) {
      submitRef.current.click();
    }
  }, []);

  const handleResend = useCallback(() => {
    if (submitRef.current?.form) {
      submitRef.current.form.otp.value = '';
    }
  }, []);

  return (
    <div
      {...customAttributes}
      className={classnames(
        className,
        {
          'mobile:hidden': mobile_hide,
          'desktop:hidden': desktop_hide,
          'pointer-events-none': isSubmitting,
        },
        styles.loginElement,
        customAttributes?.className,
        'flex flex-col gap-2',
      )}
      style={
        {
          width: '100%',
          maxWidth: width,
          gap: gap,
          color: textColor,
          borderColor: borderColor,
          borderWidth: borderWidth,
          borderRadius: borderRadius,
          borderStyle: borderStyle,
          padding: padding,
          backgroundColor: backgroundColor,
          boxSizing: 'border-box',
          overflow: 'hidden',
          fontFamily: computeFont(fontFamily) || 'inherit',
          '--input-background-color': inputBackgroundColor,
        } as CSSProperties
      }
      {...attributes}
    >
      <input type="hidden" name="email" value={email} />
      <div className="flex flex-col w-full text-center gap-y-6">
        <label htmlFor="otp" className="font-bold">
          {otp_label?.replace('{{email}}', email)}
        </label>
        <OTPInput name="otp" maxLength={6} onComplete={handleSubmit} />
      </div>
      <input
        ref={submitRef}
        type="submit"
        disabled={isSubmitting}
        className="cursor-pointer disabled:opacity-50 disabled:pointer-events-none"
        style={
          {
            borderRadius: buttonBorderRadius,
            borderColor: buttonBorderColor,
            borderWidth: buttonBorderWidth,
            borderStyle: buttonBorderStyle,
            backgroundColor: buttonBackgroundColor,
            color: buttonTextColor,
            fontSize: buttonFontSize,
            boxSizing: 'border-box',
            height: height || '50px',
            padding: '0 16px 0 16px',
          } as CSSProperties
        }
        value={isSubmitting ? buttonSubmittingText : buttonText}
      />

      <button
        type="submit"
        disabled={isSubmitting}
        className="cursor-pointer disabled:opacity-50 disabled:pointer-events-none"
        style={
          {
            borderRadius: buttonBorderRadius,
            borderColor: buttonBackgroundColor,
            borderWidth: buttonBorderWidth,
            borderStyle: 'solid',
            backgroundColor: buttonTextColor,
            color: buttonBackgroundColor,
            fontSize: buttonFontSize,
            boxSizing: 'border-box',
            height: height || '50px',
            padding: '0 16px 0 16px',
          } as CSSProperties
        }
        name="intent"
        value="resend_otp"
        onClick={handleResend}
      >
        {resend_otp_button_label}
      </button>
      {children}
    </div>
  );
}
